import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Button, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "flowbite-react";
import { HiPencil, HiTrash } from "react-icons/hi";
import { MdDragIndicator } from "react-icons/md";
import { toast } from "react-toastify";
import AsyncSelect from "react-select/async";
import debounce from "lodash/debounce";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  TouchSensor,
  MouseSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { isEqual } from "lodash";
import * as Sentry from "@sentry/react";

import SortableItem from "../pages/programs/SortableItem";
import { isCancelReq } from "../util/axios";
import { blueThemeSpinner } from "../pages/exercises/ExerciseModal";

const IGNORE_TAGS = ["BUTTON"];

// Block Dnd, if IGNORE_TAGS includes elements tag or element has data-no-dnd attribute
const customHandleEvent = (element: HTMLElement | null) => {
  let cur = element;

  while (cur) {
    if (IGNORE_TAGS.includes(cur.tagName) || cur.dataset["noDnd"]) {
      return false;
    }
    cur = cur.parentElement;
  }

  return true;
};

MouseSensor.activators = [
  {
    eventName: "onMouseDown",
    handler: ({ nativeEvent: event }: { nativeEvent: MouseEvent }) =>
      customHandleEvent(event.target as HTMLElement),
  },
];

TouchSensor.activators = [
  {
    eventName: "onTouchStart",
    handler: ({ nativeEvent: event }: { nativeEvent: TouchEvent }) =>
      customHandleEvent(event.target as HTMLElement),
  },
];

interface SectionItem {
  id: number;
  title: string;
  order: number;
  key: number;
}

interface EditSectionModalConfig {
  /** The entity type name (e.g., "Workout", "Athlete") */
  entityName: string;
  /** Plural form of the entity name (e.g., "Workouts", "Athletes") */
  entityNamePlural: string;
  /** Function to fetch all items for the dropdown (with search support) */
  fetchItems: (params: {
    page: number;
    take: number;
    search?: string;
    dropdown: boolean;
  }) => Promise<{
    data: {
      results: {
        data?: { id: number; title: string; order: number | null }[];
        trainers?: { id: number; title: string; order: number | null }[];
        total: number;
      };
    };
  }>;
  /** Function to save the section order */
  saveSection: (items: { id: number; order: number }[]) => Promise<any>;
  /** The key in the response to get items from (e.g., "data" for workouts, "trainers" for trainers) */
  responseKey: "data" | "trainers";
}

interface EditSectionModalProps {
  config: EditSectionModalConfig;
}

const EditSectionModal = function ({ config }: EditSectionModalProps) {
  const { entityName, entityNamePlural, fetchItems, saveSection, responseKey } =
    config;

  const [isOpen, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const [sectionItems, setSectionItems] = useState<SectionItem[]>([]);
  const [originalSectionItems, setOriginalSectionItems] = useState<
    SectionItem[]
  >([]);

  const rootRef = useRef<HTMLDivElement>(null);
  const keyCounterRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const hasChanged = useMemo(() => {
    return !isEqual(originalSectionItems, sectionItems);
  }, [originalSectionItems, sectionItems]);

  const getItemsFromResponse = (response: any): any[] => {
    const results = response.data.results;
    return results[responseKey] || results.data || [];
  };

  const loadDropdownItems = useCallback(
    async (query: string) => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      try {
        const response = await fetchItems({
          page: 1,
          take: 100,
          search: query,
          dropdown: true,
        });
        // Filter out items that are already in the section
        const allItems = getItemsFromResponse(response);
        const filteredItems = allItems.filter(
          (item: any) => !sectionItems.some((si) => si.id === item.id),
        );
        // Return items formatted for dropdown
        return filteredItems.map((item: any) => ({
          label: item.title,
          value: item.id,
          order: item.order,
        }));
      } catch (error) {
        if (!isCancelReq(error)) {
          console.error(
            `Error loading ${entityNamePlural.toLowerCase()}:`,
            error,
          );
        }
        return [];
      }
    },
    [sectionItems, fetchItems, entityNamePlural, responseKey],
  );

  // Debounced version of loadDropdownItems
  const debouncedLoadItems = useMemo(
    () =>
      debounce((query: string, callback: (options: any[]) => void) => {
        loadDropdownItems(query).then(callback);
      }, 300),
    [loadDropdownItems],
  );

  useEffect(() => {
    if (!isOpen) return;

    const init = async () => {
      try {
        setLoading(true);
        const response = await fetchItems({
          page: 1,
          take: 100,
          dropdown: true,
        });
        // Filter items with non-null order and sort by order
        const allItems = getItemsFromResponse(response);
        const orderedItems = allItems
          .filter((item: any) => item.order !== null)
          .sort((a: any, b: any) => a.order - b.order)
          .map((item: any) => {
            const uniqueKey = keyCounterRef.current++;
            return {
              id: item.id,
              title: item.title,
              order: item.order,
              key: uniqueKey,
            };
          });

        setSectionItems(orderedItems);
        setOriginalSectionItems([...orderedItems]);
      } catch (error) {
        if (!isCancelReq(error)) {
          console.log(error);
        }
      } finally {
        setLoading(false);
      }
    };

    init();

    // Cleanup function to cancel any pending requests
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      debouncedLoadItems.cancel();
    };
  }, [isOpen, fetchItems, responseKey]);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      // Create payload with zero-based ordering
      const payload = sectionItems.map((item, index) => ({
        id: item.id,
        order: index,
      }));

      await saveSection(payload);
      setOriginalSectionItems([...sectionItems]);
      setOpen(false);
      toast.success(`${entityName} Section Saved Successfully!`);
    } catch (error: any) {
      console.error(
        `Failed to save ${entityName.toLowerCase()} section:`,
        error,
      );
      Sentry.captureException(error, {
        tags: {
          component: "EditSectionModal",
          action: "saveSection",
          entityName,
        },
        extra: {
          itemCount: sectionItems.length,
        },
      });
      const errorMessage = error?.response?.data?.message || error?.message;
      toast.error(
        errorMessage
          ? `Failed to save ${entityName.toLowerCase()} section: ${errorMessage}`
          : `Failed to save ${entityName.toLowerCase()} section. Please try again.`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSectionItems((items) => {
        const oldIndex = items.findIndex((item) => item.key === active.id);
        const newIndex = items.findIndex((item) => item.key === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        return newItems.map((item, index) => ({
          ...item,
          order: index,
        }));
      });
    }
  };

  const assignOrder = (items: SectionItem[]) =>
    items.map((item, i) => ({ ...item, order: i }));

  return (
    <>
      <div ref={rootRef}>
        <Button color="primary" onClick={() => setOpen(true)}>
          <div className="flex items-center gap-x-3">
            <HiPencil className="text-xl" />
            Edit {entityName} Section
          </div>
        </Button>
        <Modal
          onClose={() => setOpen(false)}
          root={rootRef.current ?? undefined}
          show={isOpen}
          size="xl"
          position="top-center"
        >
          <ModalHeader className="border-b border-gray-200 p-6! dark:border-gray-700">
            <strong>Edit {entityName} Section</strong>
          </ModalHeader>
          <ModalBody>
            <div className="mb-4">
              <Label className="text-base">Add {entityName} to Section</Label>
              <AsyncSelect
                isClearable={true}
                className="mt-2"
                defaultValue={[]}
                loadOptions={debouncedLoadItems}
                noOptionsMessage={() => "No results"}
                menuPortalTarget={document.body}
                onChange={(v: any) => {
                  if (v == null) return;
                  // Check if item is already in the list
                  const exists = sectionItems.some(
                    (item) => item.id === v.value,
                  );
                  if (exists) {
                    toast.warning(
                      `This ${entityName.toLowerCase()} is already in the section`,
                    );
                    return;
                  }
                  const newKey = keyCounterRef.current++;
                  setSectionItems(
                    assignOrder([
                      ...sectionItems,
                      {
                        id: v.value,
                        title: v.label,
                        order: sectionItems.length,
                        key: newKey,
                      },
                    ]),
                  );
                }}
                styles={{
                  control: (base) => ({
                    ...base,
                    backgroundColor: "#f9fafb",
                    borderColor: "rgb(156, 163, 175)",
                  }),
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>

            <div className="">
              {loading ? (
                <div className="flex h-12 flex-row items-center justify-center">
                  <Spinner
                    aria-label="Loading..."
                    size="lg"
                    theme={blueThemeSpinner}
                  />
                </div>
              ) : (
                <>
                  {sectionItems?.length === 0 ? (
                    <p className="text-center">
                      No {entityNamePlural.toLowerCase()} in section
                    </p>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={onDragEnd}
                    >
                      <SortableContext
                        items={sectionItems.map((item) => item.key)}
                        strategy={verticalListSortingStrategy}
                      >
                        {sectionItems.map((item) => (
                          <SortableItem key={item.key} id={item.key}>
                            {({
                              setNodeRef,
                              attributes,
                              listeners,
                              style,
                            }: any) => (
                              <div
                                ref={setNodeRef}
                                style={style}
                                className="mt-3 flex flex-row items-center gap-1 rounded-lg border border-gray-400 bg-gray-50 px-4 py-3 text-base text-gray-800 dark:bg-gray-800 dark:text-gray-300"
                              >
                                <MdDragIndicator
                                  className="cursor-grab inline text-xl text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                  {...attributes}
                                  {...listeners}
                                />
                                <span className="ml-2">{item.title}</span>
                                <div className="ml-auto flex flex-row items-center">
                                  <button
                                    type="button"
                                    className="relative inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-500 hover:text-red-600 hover:bg-red-100 dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-900/30 transition-colors"
                                    data-no-dnd="true"
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={() => {
                                      const updatedItems = sectionItems.filter(
                                        (si) => si.key !== item.key,
                                      );
                                      setSectionItems(
                                        assignOrder(updatedItems),
                                      );
                                    }}
                                  >
                                    <HiTrash className="text-xl relative z-10" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </SortableItem>
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}
                </>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !hasChanged}
              className="px-4"
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </>
  );
};

export default EditSectionModal;
